import React, { useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Loader, Save, RefreshCw, ExternalLink, Users, Receipt, Clock } from 'lucide-react';

const MASKED = '••••••••';

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

function fmtRelative(d) {
  if (!d) return '';
  const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// The cron runs at fixed :00/:30 marks (backend cronJobs.js `*/30 * * * *`), not "30 min
// after the last run" — so the next run is derivable purely from wall-clock time, no extra
// API call needed.
function nextSyncEta() {
  const now = new Date();
  const next = new Date(now);
  next.setSeconds(0, 0);
  next.setMinutes(now.getMinutes() < 30 ? 30 : 60);
  const mins = Math.max(0, Math.round((next - now) / 60000));
  return mins <= 1 ? 'within a minute' : `in ~${mins} min`;
}

const SECTION = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{
      fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14,
      paddingBottom: 6, borderBottom: '1px solid var(--border)'
    }}>{title}</div>
    {children}
  </div>
);

// One-way live sync (Customers + this month's Payments) into a Google Sheet the user
// owns. Requires a Google Cloud "service account" — there's no way around this, it's how
// any app writes to a Sheet without the user manually authorizing every request. See
// backend's services/googleSheetsSyncService.js / IntegrationSettings.googleSheets.
export default function GoogleSheetsSettingsTab() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [sheetId, setSheetId] = useState('');
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [syncMode, setSyncMode] = useState('periodic');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setLoadingStatus(true);
    try {
      const { data } = await settingsAPI.getGoogleSheets();
      setStatus(data.data);
      setSheetId(data.data?.sheetId || '');
      setIsEnabled(!!data.data?.isEnabled);
      setSyncMode(data.data?.syncMode || 'periodic');
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.message || 'Failed to load Google Sheets settings');
    } finally {
      if (!silent) setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Background refresh while this tab is open — the sync happens on a server cron the
  // admin isn't triggering, so polling is the only way this panel reflects a run that
  // just happened without them manually hitting refresh.
  useEffect(() => {
    const id = setInterval(() => loadStatus(true), 30000);
    return () => clearInterval(id);
  }, [loadStatus]);

  async function handleSave() {
    setSaving(true);
    try {
      const payload = { sheetId: sheetId.trim(), isEnabled, syncMode };
      // Only send the JSON if the user actually typed something new — leaving the masked
      // placeholder untouched means "keep the credential already on file" (same convention
      // as the Netzur Token / GTPL cookie fields elsewhere in Settings).
      if (serviceAccountJson.trim() && serviceAccountJson.trim() !== MASKED) {
        payload.serviceAccountJson = serviceAccountJson.trim();
      }
      await settingsAPI.saveGoogleSheets(payload);
      toast.success('Google Sheets settings saved');
      setServiceAccountJson('');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleTestNow() {
    setTesting(true);
    try {
      const { data } = await settingsAPI.testGoogleSheets();
      toast.success(data.message || 'Synced successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync test failed');
    } finally {
      setTesting(false);
      loadStatus();
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <SECTION title="One-time Google Cloud setup">
          <ol style={{ fontSize: '.85rem', color: 'var(--text-muted)', paddingLeft: 18, marginBottom: 4, lineHeight: 1.8 }}>
            <li>Go to <strong>console.cloud.google.com</strong> and create (or pick) a project.</li>
            <li><strong>APIs & Services → Library</strong> → enable the <strong>Google Sheets API</strong>.</li>
            <li><strong>APIs & Services → Credentials → Create Credentials → Service Account</strong> — give it any name, no roles needed → Done.</li>
            <li>Open the new service account → <strong>Keys</strong> tab → <strong>Add Key → JSON</strong> — downloads a file.</li>
            <li>Open your target Google Sheet → <strong>Share</strong> → paste the service account's email
              (inside the JSON, the <code>client_email</code> field, looks like
              <code> name@project.iam.gserviceaccount.com</code>) → give it <strong>Editor</strong> access.</li>
            <li>Copy the Sheet's ID from its URL: <code>docs.google.com/spreadsheets/d/<strong>SHEET_ID</strong>/edit</code>.</li>
            <li>Paste the whole downloaded JSON file's contents, and the Sheet ID, below.</li>
          </ol>
        </SECTION>

        <SECTION title="Status">
          {loadingStatus ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
              <Loader size={13} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />Loading…
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '.85rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {status?.sheetId
                    ? <><CheckCircle size={13} style={{ color: 'var(--success)' }} />Configured</>
                    : <><XCircle size={13} style={{ color: 'var(--danger)' }} />Not configured yet</>}
                  {status?.sheetId && (
                    <span style={{
                      fontSize: '.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                      background: status.isEnabled ? 'var(--cb-d1fae5)' : 'var(--cb-f3f4f6)',
                      color: status.isEnabled ? 'var(--c-059669)' : 'var(--text-muted)',
                    }}>
                      {status.isEnabled ? 'RUNNING' : 'OFF'}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {status?.sheetId && (
                    <a
                      href={`https://docs.google.com/spreadsheets/d/${status.sheetId}/edit`}
                      target="_blank" rel="noreferrer"
                      className="btn btn-sm btn-outline-secondary"
                      style={{ fontSize: '.78rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <ExternalLink size={12} /> Open Sheet
                    </a>
                  )}
                  {status?.sheetId && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleTestNow}
                      disabled={testing}
                      style={{ fontSize: '.78rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <RefreshCw size={12} style={testing ? { animation: 'spin 1s linear infinite' } : undefined} />
                      {testing ? 'Syncing…' : 'Sync Now'}
                    </button>
                  )}
                </div>
              </div>

              {status?.lastSyncResult && (
                <div style={{ color: status.lastSyncResult === 'failed' ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                  {status.lastSyncResult === 'failed'
                    ? <><XCircle size={12} style={{ marginRight: 5, verticalAlign: -1 }} />Last sync failed</>
                    : <><CheckCircle size={12} style={{ marginRight: 5, verticalAlign: -1 }} />Last sync succeeded</>}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                    {status.lastSyncedAt ? ` · ${fmtRelative(status.lastSyncedAt)} (${fmtDateTime(status.lastSyncedAt)})` : ''}
                  </span>
                </div>
              )}
              {status?.lastSyncResult === 'failed' && status?.lastSyncError && (
                <div style={{ color: 'var(--text-muted)' }}>{status.lastSyncError}</div>
              )}

              {status?.lastSyncResult === 'success' && (
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}>
                    <Users size={13} /> {status.lastSyncCustomerCount ?? '—'} customers
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}>
                    <Receipt size={13} /> {status.lastSyncPaymentCount ?? '—'} payments (this month)
                  </span>
                  {status.lastSyncActiveCatvTotal != null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}>
                      <Receipt size={13} /> ACTIVE_CATV: {status.lastSyncActiveCatvMatched ?? 0}/{status.lastSyncActiveCatvTotal} matched
                      {!!status.lastSyncActiveCatvRenamed && ` (${status.lastSyncActiveCatvRenamed} account no. auto-corrected)`}
                      {!!status.lastSyncActiveCatvAdded && `, ${status.lastSyncActiveCatvAdded} new row(s) added`}
                    </span>
                  )}
                  {!!status.lastSyncActiveCatvConflicts && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--danger)', fontWeight: 600 }}>
                      <XCircle size={13} /> {status.lastSyncActiveCatvConflicts} account no. collision(s) skipped — see server log
                    </span>
                  )}
                  {status.lastSyncActiveWifiTotal != null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}>
                      <Receipt size={13} /> ACTIVE_WIFI: {status.lastSyncActiveWifiMatched ?? 0}/{status.lastSyncActiveWifiTotal} matched
                      {!!status.lastSyncActiveWifiRenamed && ` (${status.lastSyncActiveWifiRenamed} username auto-corrected)`}
                      {!!status.lastSyncActiveWifiAdded && `, ${status.lastSyncActiveWifiAdded} new row(s) added`}
                    </span>
                  )}
                  {!!status.lastSyncActiveWifiConflicts && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--danger)', fontWeight: 600 }}>
                      <XCircle size={13} /> {status.lastSyncActiveWifiConflicts} username collision(s) skipped — see server log
                    </span>
                  )}
                </div>
              )}

              {status?.isEnabled && status?.syncMode !== 'realtime' && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}>
                  <Clock size={13} /> Next automatic sync {nextSyncEta()}
                </div>
              )}
              {status?.isEnabled && status?.syncMode === 'realtime' && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}>
                  <Clock size={13} /> Syncs within ~10s of any customer/payment change
                </div>
              )}

              <div style={{ color: 'var(--text-muted)' }}>
                {status?.syncMode === 'realtime'
                  ? 'Real-time mode: every customer or payment save queues a fresh sync (batched ~10s so a burst of changes only triggers one write).'
                  : 'Periodic mode: runs automatically every 30 minutes once enabled.'}
                {' '}Customers (full list) and Payments (this month), each overwriting its own tab in the sheet.
              </div>
            </div>
          )}
        </SECTION>

        <SECTION title="Connection">
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 600, marginBottom: 6 }}>Sheet ID</label>
            <input
              type="text"
              className="form-control"
              placeholder="1AbCdeFGhijKLmNoPQRstuVWxyz..."
              value={sheetId}
              onChange={e => setSheetId(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '.85rem' }}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 600, marginBottom: 6 }}>Service Account JSON</label>
            <textarea
              className="form-control font-mono"
              rows={5}
              placeholder={status?.serviceAccountJson ? MASKED : '{ "type": "service_account", "project_id": "...", ... }'}
              value={serviceAccountJson}
              onChange={e => setServiceAccountJson(e.target.value)}
              style={{ fontSize: '.78rem' }}
            />
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
              Leave blank to keep the credential already on file — only fill this in when
              setting up for the first time or rotating the key.
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={isEnabled} onChange={e => setIsEnabled(e.target.checked)} />
            Enabled
          </label>

          <div>
            <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 600, marginBottom: 6 }}>Sync mode</label>
            <div style={{ display: 'flex', gap: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="radio" name="syncMode" value="periodic" checked={syncMode === 'periodic'} onChange={() => setSyncMode('periodic')} />
                Every 30 minutes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="radio" name="syncMode" value="realtime" checked={syncMode === 'realtime'} onChange={() => setSyncMode('realtime')} />
                Real-time (on every change)
              </label>
            </div>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
              Only one mode runs at a time — switching to real-time turns off the 30-minute cron for this sync, and vice versa.
            </div>
          </div>
        </SECTION>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !sheetId.trim()}>
            <Save size={14} style={{ marginRight: 6 }} />
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
